import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { Add, Backspace, DeleteOutline } from '@mui/icons-material';
import {
  Catalogue, FormulaCheck, MeasureRef, PART_NAMES, indicatorRegistryApi,
} from '../../../services/indicatorRegistryApi';
import MeasurePicker from './MeasurePicker';

interface Props {
  catalogue: Catalogue;
  terms: Record<string, MeasureRef>;
  formula: string;
  onChange: (terms: Record<string, MeasureRef>, formula: string) => void;
}

/** Starting points people can click instead of typing. */
const PATTERNS: { label: string; formula: string; hint: string }[] = [
  { label: 'A ÷ B × 100', formula: 'A ÷ B × 100', hint: 'A percentage of B' },
  { label: 'A ÷ B', formula: 'A ÷ B', hint: 'A ratio, e.g. pupils per classroom' },
  { label: '(A + B) ÷ C', formula: '(A + B) ÷ C', hint: 'Two figures together, per a third' },
  { label: '(A − B) ÷ B × 100', formula: '(A − B) ÷ B × 100', hint: 'Change from B to A, in %' },
  { label: 'A ÷ (B + C) × 100', formula: 'A ÷ (B + C) × 100', hint: 'Share of a combined total' },
];

const OPERATORS: { show: string; insert: string; tip: string }[] = [
  { show: '+', insert: ' + ', tip: 'plus' },
  { show: '−', insert: ' − ', tip: 'minus' },
  { show: '×', insert: ' × ', tip: 'times' },
  { show: '÷', insert: ' ÷ ', tip: 'divided by' },
  { show: '(', insert: '(', tip: 'open bracket' },
  { show: ')', insert: ')', tip: 'close bracket' },
  { show: '× 100', insert: ' × 100', tip: 'as a percentage' },
];

export function partLabel(part: MeasureRef | undefined, catalogue: Catalogue): string {
  if (!part) return '';
  return part.label?.trim() || catalogue.measures.find(m => m.code === part.measure)?.label || part.measure;
}

/**
 * Build an indicator from several named parts and a formula over them,
 * e.g. (Girls enrolled + Boys enrolled) ÷ Classrooms. The formula is checked
 * as it is typed and read back in words.
 */
const FormulaBuilder: React.FC<Props> = ({ catalogue, terms, formula, onChange }) => {
  const input = useRef<HTMLInputElement>(null);
  const [check, setCheck] = useState<FormulaCheck | null>(null);
  const names = PART_NAMES.filter(n => n in terms);
  const labels = Object.fromEntries(names.map(n => [n, partLabel(terms[n], catalogue)]));
  const labelsKey = JSON.stringify(labels);

  // Ask the server to read the formula, a moment after typing stops.
  useEffect(() => {
    if (!formula.trim()) {
      setCheck(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      indicatorRegistryApi.checkFormula(formula, labels).then(setCheck).catch(() => setCheck(null));
    }, 350);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formula, labelsKey]);

  const setPart = (name: string, part: MeasureRef) => onChange({ ...terms, [name]: part }, formula);

  const addPart = () => {
    const name = PART_NAMES.find(n => !(n in terms));
    if (!name) return;
    const first = catalogue.measures[0]?.code || '';
    onChange({ ...terms, [name]: { measure: first, filters: {}, label: '' } }, formula);
  };

  const removePart = (name: string) => {
    const next = { ...terms };
    delete next[name];
    onChange(next, formula);
  };

  /** Put text where the cursor is, or at the end. */
  const insert = (text: string) => {
    const el = input.current;
    const start = el?.selectionStart ?? formula.length;
    const end = el?.selectionEnd ?? formula.length;
    const next = (formula.slice(0, start) + text + formula.slice(end)).replace(/ {2,}/g, ' ');
    onChange(terms, next);
    requestAnimationFrame(() => {
      if (!el) return;
      const pos = Math.min(next.length, start + text.length);
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const backspace = () => onChange(terms, formula.replace(/\s*\S\s*$/, ''));

  return (
    <Stack spacing={2}>
      {/* 1. Parts */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>1. The parts</Typography>
        <Typography variant="caption" color="text.secondary">
          Each part is one figure from SIS, TTPDS or your own data, with its own filters and year.
          Give it a short name so the formula reads in words.
        </Typography>
      </Box>
      {names.map(name => (
        <Box key={name} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
          <Chip label={name} color="primary" sx={{ fontWeight: 700, fontSize: 16, mt: 1, minWidth: 40 }} />
          <Box sx={{ flex: 1 }}>
            <TextField
              size="small" fullWidth sx={{ mb: 1 }}
              label={`Name of part ${name}`}
              placeholder={partLabel({ ...terms[name], label: '' }, catalogue)}
              value={terms[name].label || ''}
              onChange={e => setPart(name, { ...terms[name], label: e.target.value })}
            />
            <MeasurePicker
              label={`Part ${name}: what to count`}
              value={terms[name]}
              catalogue={catalogue}
              onChange={v => setPart(name, { ...v, label: terms[name].label })}
            />
          </Box>
          <Tooltip title={`Remove part ${name}`}>
            <IconButton aria-label={`Remove part ${name}`} onClick={() => removePart(name)} sx={{ mt: 0.5 }}>
              <DeleteOutline />
            </IconButton>
          </Tooltip>
        </Box>
      ))}
      <Box>
        <Button startIcon={<Add />} onClick={addPart} disabled={names.length >= PART_NAMES.length}>
          Add part {PART_NAMES.find(n => !(n in terms)) || ''}
        </Button>
      </Box>

      {/* 2. Formula */}
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>2. The formula</Typography>
        <Typography variant="caption" color="text.secondary">
          Click the buttons or type. Each part is added up for the whole area first, then the formula is applied,
          so a district's value is its own totals, not an average of its schools.
        </Typography>
      </Box>

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center', mr: 0.5 }}>Start from:</Typography>
        {PATTERNS.map(p => (
          <Tooltip key={p.formula} title={p.hint}>
            <Chip label={p.label} variant="outlined" size="small" onClick={() => onChange(terms, p.formula)} />
          </Tooltip>
        ))}
      </Stack>

      <TextField
        inputRef={input}
        fullWidth
        label="Formula"
        placeholder="e.g. (A + B) ÷ C × 100"
        value={formula}
        onChange={e => onChange(terms, e.target.value)}
        inputProps={{ style: { fontFamily: 'monospace', fontSize: 20, letterSpacing: 1 } }}
      />

      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
        {names.map(name => (
          <Tooltip key={name} title={labels[name]}>
            <Button variant="contained" size="small" sx={{ minWidth: 40, fontWeight: 700 }} onClick={() => insert(name)}>
              {name}
            </Button>
          </Tooltip>
        ))}
        {OPERATORS.map(op => (
          <Tooltip key={op.show} title={op.tip}>
            <Button variant="outlined" size="small" sx={{ minWidth: 40, fontSize: 16 }} onClick={() => insert(op.insert)}>
              {op.show}
            </Button>
          </Tooltip>
        ))}
        <Tooltip title="Remove the last item">
          <IconButton size="small" aria-label="Remove the last item" onClick={backspace}><Backspace fontSize="small" /></IconButton>
        </Tooltip>
        <Button size="small" color="inherit" onClick={() => onChange(terms, '')}>Clear</Button>
      </Stack>

      {check && check.ok && (
        <Alert severity="success">
          <Typography variant="caption" sx={{ display: 'block' }}>Reads as</Typography>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>{check.reads_as}</Typography>
          {!!check.unused?.length && (
            <Typography variant="caption" color="warning.main" sx={{ display: 'block', mt: 0.5 }}>
              Part {check.unused.join(', ')} {check.unused.length > 1 ? 'are' : 'is'} not used yet.
            </Typography>
          )}
        </Alert>
      )}
      {check && !check.ok && <Alert severity="error">{check.error}</Alert>}
    </Stack>
  );
};

export default FormulaBuilder;
